import { useState, useMemo } from "react";
import { myTasks, assignedTasks, todayTasks, overdueTasks, type TaskItem } from "@/mocks/managerWorkspace";

type SubTab = "my" | "assigned";

export default function TasksTab() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("my");
  const [statusFilter, setStatusFilter] = useState<string>("Все");

  const currentTasks: TaskItem[] = activeSubTab === "my" ? myTasks : assignedTasks;

  const filteredTasks = useMemo(() => {
    if (statusFilter === "Все") return currentTasks;
    return currentTasks.filter((t) => t.status === statusFilter);
  }, [currentTasks, statusFilter]);

  const counts = useMemo(() => {
    const all = currentTasks.length;
    const newCount = currentTasks.filter((t) => t.status === "Новая").length;
    const inProgress = currentTasks.filter((t) => t.status === "В работе").length;
    const overdue = currentTasks.filter((t) => t.status === "Просрочена").length;
    return { all, newCount, inProgress, overdue };
  }, [currentTasks]);

  const gridCols =
    activeSubTab === "my"
      ? "grid-cols-[30px_1fr_160px_100px_180px_90px]"
      : "grid-cols-[30px_1fr_150px_90px_160px_120px_90px]";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <h2 className="text-[14px] font-semibold text-[#2D2D2D] flex items-center gap-2">
            <i className="ri-task-line text-[15px] text-[#D97706]"></i>
            Задачи
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-[#2D2D2D]">
              Всего: <strong>{todayTasks}</strong>
            </span>
            <span className="w-px h-3 bg-[#D5CA8A]"></span>
            <span className="text-[11px] text-[#DC2626]">
              Просрочено: <strong>{overdueTasks}</strong>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-[#FDF2B5] rounded px-1 py-1">
            {["Все", "Новые", "В работе", "Просрочены"].map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-2 py-0.5 text-[10px] rounded whitespace-nowrap font-medium transition-colors cursor-pointer ${
                  statusFilter === f
                    ? "bg-white text-[#2D2D2D]"
                    : "text-[#666666] hover:text-[#2D2D2D]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer transition-colors">
            <i className="ri-filter-line text-[11px]"></i>
            Фильтр
          </button>
          <button className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer transition-colors">
            <i className="ri-add-line text-[11px]"></i>
            Задача
          </button>
        </div>
      </div>

      {/* Sub-tabs: Мои / Назначенные */}
      <div className="flex items-center gap-1 mb-2">
        <button
          onClick={() => { setActiveSubTab("my"); setStatusFilter("Все"); }}
          className={`px-3 py-1.5 text-[12px] rounded font-medium whitespace-nowrap cursor-pointer transition-colors ${
            activeSubTab === "my"
              ? "bg-[#FDF2B5] text-[#2D2D2D]"
              : "text-[#666666] hover:bg-[#F5F0D0]"
          }`}
        >
          Мои
          <span className="ml-1.5 text-[11px] text-[#999999]">{myTasks.length}</span>
        </button>
        <button
          onClick={() => { setActiveSubTab("assigned"); setStatusFilter("Все"); }}
          className={`px-3 py-1.5 text-[12px] rounded font-medium whitespace-nowrap cursor-pointer transition-colors ${
            activeSubTab === "assigned"
              ? "bg-[#FDF2B5] text-[#2D2D2D]"
              : "text-[#666666] hover:bg-[#F5F0D0]"
          }`}
        >
          Назначенные
          <span className="ml-1.5 text-[11px] text-[#999999]">{assignedTasks.length}</span>
        </button>
      </div>

      {/* Task list */}
      <div className="bg-white border border-[#D5CA8A] rounded overflow-hidden">
        {/* Table header */}
        <div className={`grid ${gridCols} gap-2 px-3 py-2 bg-[#FFFDF5] border-b border-[#D5CA8A] text-[10px] font-semibold text-[#666666]`}>
          <span></span>
          <span>Задача</span>
          <span>Дедлайн</span>
          <span>Приоритет</span>
          <span>Клиент</span>
          {activeSubTab === "assigned" && <span>Кому назначена</span>}
          <span>Статус</span>
        </div>

        <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
          {filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[#999999]">
              <i className="ri-inbox-line text-[28px] mb-2"></i>
              <span className="text-[12px]">Нет задач</span>
            </div>
          ) : (
            filteredTasks.map((task, idx) => (
              <div
                key={idx}
                className={`grid ${gridCols} gap-2 px-3 py-2.5 border-b border-[#E8DB6C]/30 hover:bg-[#FFFDF5] cursor-pointer transition-colors items-center`}
              >
                {/* Checkbox */}
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 rounded border border-[#D5CA8A] hover:border-[#D97706] cursor-pointer flex items-center justify-center bg-white transition-colors">
                    <i className="ri-check-line text-[10px] text-transparent"></i>
                  </div>
                </div>

                {/* Task title */}
                <span className="text-[12px] text-[#2D2D2D] font-medium truncate">
                  {task.title}
                </span>

                {/* Deadline */}
                <span
                  className={`text-[11px] ${
                    task.status === "Просрочена"
                      ? "text-[#DC2626] font-semibold"
                      : "text-[#666666]"
                  }`}
                >
                  {task.deadline}
                </span>

                {/* Priority */}
                <span>
                  {task.priority === "Высокий" && (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#FEE2E2] text-[#DC2626]">
                      Высокий
                    </span>
                  )}
                  {task.priority === "Средний" && (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#FEF3C7] text-[#D97706]">
                      Средний
                    </span>
                  )}
                  {task.priority === "Низкий" && (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#D4EDDA] text-[#2D6A4F]">
                      Низкий
                    </span>
                  )}
                </span>

                {/* Client */}
                <span className="text-[11px] text-[#666666] truncate">
                  {task.client}
                </span>

                {/* Assignee (only for assigned tab) */}
                {activeSubTab === "assigned" && (
                  <span className="text-[11px] text-[#666666] truncate">
                    {task.assignedTo || "—"}
                  </span>
                )}

                {/* Status */}
                <span>
                  {task.status === "Просрочена" && (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#FEE2E2] text-[#DC2626]">
                      Просрочена
                    </span>
                  )}
                  {task.status === "В работе" && (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#FEF3C7] text-[#D97706]">
                      В работе
                    </span>
                  )}
                  {task.status === "Новая" && (
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#D4EDDA] text-[#2D6A4F]">
                      Новая
                    </span>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between mt-3 text-[10px] text-[#666666]">
        <span>
          Показано: {filteredTasks.length} из {currentTasks.length}
        </span>
        <div className="flex items-center gap-1">
          <button className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#FDF2B5] text-[#999999] cursor-pointer">
            <i className="ri-arrow-left-s-line text-[12px]"></i>
          </button>
          <span className="px-1.5 py-0.5 rounded bg-[#FDF2B5] text-[#2D2D2D] font-medium">
            1
          </span>
          <button className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#FDF2B5] text-[#999999] cursor-pointer">
            <i className="ri-arrow-right-s-line text-[12px]"></i>
          </button>
        </div>
      </div>
    </div>
  );
}